import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import {
  Segment, Header, Progress, Button
} from 'semantic-ui-react';
import { withTranslation } from 'react-i18next';

// redux
import { push } from 'connected-react-router';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import {
  updateUserProfile,
  updateUser
} from '../../../modules/user';
import { authUtil } from '../../../utils';

import DisplayNameGetter from './fields/displayName';
import CountryGetter from './fields/country';
import YoBGetter from './fields/yob';
import GenresGetter from './fields/genresGetter';
import LanguagesGetter from './fields/languages';
import BioGetter from './fields/bio';
import EmailPreferenceGetter from './fields/emailPreferences';
import ReaderListConsentGetter from './fields/readerListConsent';
import RoleGetter from './fields/role';
import AiManuscriptPreference from './fields/aiManuscriptPreference';

const ALL_STEPS = [
  'displayName',
  'role',
  'country',
  'yob',
  'preferredGenres',
  'preferredLanguages',
  'showAiGeneratedManuscripts',
  'bio',
  'emailPreferences',
  'readerListConsent',
];

export class UserProfileBuilder extends Component {

  constructor(props) {
    super(props);
    this.state = {
      currentStepIndex: null,
    };
  }

  componentDidMount() {
    if (this.props.onAsked) {
      this.props.onAsked();
    }
    this._initializeStepIndex();
  }

  componentDidUpdate() {
    if (this.state.currentStepIndex === null) {
      this._initializeStepIndex();
    }
  }

  _initializeStepIndex() {
    const { missingInformation, userProfile } = this.props;
    if (!missingInformation || missingInformation.length === 0) return;
    const relevantSteps = this._getRelevantSteps(userProfile);
    const firstMissingIndex = relevantSteps.findIndex(
      step => missingInformation.includes(step)
    );
    if (firstMissingIndex >= 0) {
      this.setState({ currentStepIndex: firstMissingIndex });
    }
  }

  _getRelevantSteps(userProfile) {
    const role = userProfile && userProfile.role;
    return ALL_STEPS.filter(step => {
      // preferredLanguages only relevant for readers, not pure writers
      if (['preferredLanguages', 'showAiGeneratedManuscripts'].includes(step)
        && role === 'WRITER') return false;
      return true;
    });
  }

  getGenderOptions = () => {
    const { t } = this.props;
    const genderOptions = [
      {
        key: 'f', value: 'f', text: t('female')
      },
      {
        key: 'm', value: 'm', text: t('male')
      },
      {
        key: 'o', value: 'o', text: t('other')
      },
      {
        key: 'na', value: 'na', text: t('DontWantToShare')
      }
    ];
    return genderOptions;
  }

  currentThingToAskFor = () => {
    const { userProfile, updatingUserProfile } = this.props;
    const { currentStepIndex } = this.state;
    const { countryOptions, languageOptions } = this.state;
    const relevantSteps = this._getRelevantSteps(userProfile);

    if (currentStepIndex === null || currentStepIndex >= relevantSteps.length) {
      return null;
    }

    const step = relevantSteps[currentStepIndex];

    switch (step) {
      case 'displayName': {
        const initialName = (userProfile && userProfile.displayName)
          ? userProfile.displayName
          : (localStorage.getItem('br-pending-display-name') || '');
        return (
          <DisplayNameGetter
            initialValue={initialName}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('displayName')}
            wizardMode
          />
        );
      }
      case 'role':
        return (
          <RoleGetter
            initialValue={userProfile && userProfile.role}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('role')}
            wizardMode
          />
        );
      case 'country':
        return (
          <CountryGetter
            options={countryOptions}
            initialValue={userProfile && userProfile.country}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('country')}
            wizardMode
          />
        );
      case 'yob':
        return (
          <YoBGetter
            initialValue={userProfile && userProfile.yob}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('yob')}
            wizardMode
          />
        );
      case 'preferredGenres':
        return (
          <GenresGetter
            initialValue={userProfile && userProfile.preferredGenres}
            onUpdate={value => this.updateProfile({ preferredGenres: value })}
            updating={updatingUserProfile.includes('preferredGenres')}
            wizardMode
          />
        );
      case 'preferredLanguages':
        return (
          <LanguagesGetter
            options={languageOptions}
            initialValue={userProfile && userProfile.preferredLanguages}
            onUpdate={value => this.updateProfile({ preferredLanguages: value })}
            updating={updatingUserProfile.includes('preferredLanguages')}
            wizardMode
          />
        );
      case 'showAiGeneratedManuscripts':
        return (
          <AiManuscriptPreference
            initialValue={userProfile?.showAiGeneratedManuscripts}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('showAiGeneratedManuscripts')}
            wizardMode
          />
        );
      case 'bio':
        return (
          <BioGetter
            initialValue={userProfile && userProfile.bio}
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('bio')}
            wizardMode
          />
        );
      case 'emailPreferences':
        return (
          <EmailPreferenceGetter
            onUpdate={this.updateProfile}
            emailPreferences={userProfile && userProfile.emailPreferences}
            updating={updatingUserProfile.includes('emailPreferences')}
            wizardMode
          />
        );
      case 'readerListConsent':
        return (
          <ReaderListConsentGetter
            onUpdate={this.updateProfile}
            updating={updatingUserProfile.includes('readerListConsent')}
            wizardMode
          />
        );
      default:
        return null;
    }
  }

  updateProfile = async (data) => {
    if (data.displayName) {
      localStorage.removeItem('br-pending-display-name');
    }
    const idToken = await authUtil.getFreshIdToken();
    this.props.updateUserProfile(idToken, { ...data }, success => {
      if (!success) return;
      // Advance only after the saved choice is confirmed.
      const { userProfile } = this.props;
      const relevantSteps = this._getRelevantSteps(userProfile);
      this.setState(prev => ({
        currentStepIndex: Math.min(
          (prev.currentStepIndex || 0) + 1,
          relevantSteps.length
        ),
      }));
    });
  }

  handleBack = () => {
    this.setState(prev => ({
      currentStepIndex: Math.max(0, (prev.currentStepIndex || 0) - 1),
    }));
  }

  // render
  render() {
    const { t, onSkip } = this.props;
    const { userProfile } = this.props;
    const { currentStepIndex } = this.state;
    const relevantSteps = this._getRelevantSteps(userProfile);
    const totalSteps = relevantSteps.length;
    const currentStep = currentStepIndex !== null ? currentStepIndex + 1 : 1;

    return (
      <React.Fragment>
        <div
          style={{
            minWidth: 'calc(100vw / 2)', maxWidth: 500, padding: '1em'
          }}
        >
          <Header
            content={(
              <React.Fragment>
                <span>
                  {t('UpdateProfileHeader')}
                </span>
              </React.Fragment>
            )}
          />
          {totalSteps > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25em' }}>
                <small style={{ color: '#888' }}>{currentStep} / {totalSteps}</small>
              </div>
              <Progress value={currentStep} total={totalSteps} color='orange' size='tiny' style={{ marginBottom: '0.5em' }} />
            </div>
          )}
          <Segment raised clearing color='orange'>
            {this.currentThingToAskFor()}
          </Segment>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75em' }}>
            {currentStepIndex > 0 ? (
              <Button basic size='small' icon='arrow left' content={t('Back')} onClick={this.handleBack} />
            ) : (
              <div />
            )}
            {onSkip && (
              <Button basic size='small' onClick={onSkip}>
                {t('DoThisLater')}
              </Button>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

// redux stuff
const mapStateToProps = state => ({
  user: state.user.user,
  userProfile: state.user.userProfile,
  updatingUserProfile: state.user.updatingUserProfile,
  currentLanguage: state.user.currentLanguage
});

const mapDispatchToProps = dispatch => bindActionCreators({
  updateUserProfile,
  updateUser,
  changePage: newPage => push(newPage)
}, dispatch)

export default withTranslation()(withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(UserProfileBuilder)))
